import { Card } from "@devhop/ui";
import { CheckCircle2, XCircle, AlertCircle, AlertTriangle } from "lucide-react";
import type { AuditSummaryStats } from "@/modules/internal-audit/domain/repo/summary/get-stats";
import { Button } from "@devhop/ui";

type AuditSummaryStatsCardsProps = {
  stats: AuditSummaryStats;
  onViewUnresolvedIssues: () => void;
};

export function AuditSummaryStatsCards({
  stats,
  onViewUnresolvedIssues,
}: AuditSummaryStatsCardsProps) {
  const { overall, unresolvedIssues } = stats;

  return (
    <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4" data-tourid="summary-stats-cards">
      <Card className="p-6">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-muted-foreground text-sm">ຈຳນວນການກວດສອບທັງໝົດ</p>
            <p className="mt-2 text-3xl font-bold">{overall.totalAudits}</p>
          </div>
          <CheckCircle2 className="h-8 w-8 text-blue-500" />
        </div>
      </Card>

      <Card className="p-6">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-muted-foreground text-sm">ຜ່ານ</p>
            <p className="mt-2 text-3xl font-bold text-green-600">
              {overall.passCount}
            </p>
            <p className="text-muted-foreground text-xs">
              {overall.passRate}% ອັດຕາຜ່ານ
            </p>
          </div>
          <CheckCircle2 className="h-8 w-8 text-green-500" />
        </div>
      </Card>

      <Card className="p-6">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-muted-foreground text-sm">ບໍ່ຜ່ານ</p>
            <p className="mt-2 text-3xl font-bold text-red-600">
              {overall.failCount}
            </p>
            <p className="text-muted-foreground text-xs">
              {overall.totalAudits > 0
                ? Math.round((overall.failCount / overall.totalAudits) * 100)
                : 0}
              % ອັດຕາບໍ່ຜ່ານ
            </p>
          </div>
          <XCircle className="h-8 w-8 text-red-500" />
        </div>
      </Card>

      <Card className="p-6">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-muted-foreground text-sm">ຕ້ອງປັບປຸງ</p>
            <p className="mt-2 text-3xl font-bold text-yellow-600">
              {overall.needsImprovementCount}
            </p>
            <p className="text-muted-foreground text-xs">
              {overall.totalAudits > 0
                ? Math.round(
                    (overall.needsImprovementCount / overall.totalAudits) * 100,
                  )
                : 0}
              % ອັດຕາຕ້ອງປັບປຸງ
            </p>
          </div>
          <AlertCircle className="h-8 w-8 text-yellow-500" />
        </div>
      </Card>

      {unresolvedIssues.total > 0 && (
        <Card className="col-span-full p-6 border-orange-200 bg-orange-50">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-muted-foreground text-sm">
                ບັນຫາທີ່ຍັງບໍ່ແກ້ໄຂ
              </p>
              <p className="mt-2 text-3xl font-bold text-orange-600">
                {unresolvedIssues.total}
              </p>
              <div className="mt-2 flex gap-4 text-xs">
                {unresolvedIssues.critical > 0 && (
                  <span className="text-red-600">
                    ວິກິດ: {unresolvedIssues.critical}
                  </span>
                )}
                {unresolvedIssues.high > 0 && (
                  <span className="text-orange-600">
                    ສູງ: {unresolvedIssues.high}
                  </span>
                )}
                {unresolvedIssues.medium > 0 && (
                  <span className="text-yellow-600">
                    ກາງ: {unresolvedIssues.medium}
                  </span>
                )}
                {unresolvedIssues.low > 0 && (
                  <span className="text-blue-600">
                    ຕ່ຳ: {unresolvedIssues.low}
                  </span>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <AlertTriangle className="h-8 w-8 text-orange-500" />
              <Button variant="outline" size="sm" onClick={onViewUnresolvedIssues}>
                ເບິ່ງບັນຫາ
              </Button>
            </div>
          </div>
        </Card>
      )}
    </div>
  );
}

